/**
 * 광고 구매 단계 계측(퍼널) — 순수 부분(클라이언트·서버 공용 · 의존 없음).
 *  기록: user_events event_type = 'ads_funnel' · event_name = 단계 키(아래 ADS_FUNNEL_STEPS) · properties = 작은 값만(상품·기간·사유 코드).
 *   ⛔ 개인정보(상호·메일·전화·사업자번호)를 properties 에 싣지 않는다.
 *  보기: 어드민 «광고 현황 › 퍼널» — 단계마다 «방문자 수»(visitor_id 기준 · 같은 사람이 여러 번 눌러도 1).
 *   광고 안내 방문자는 같은 표(user_events)의 page_view(/advertise)에서 센다. 구매 화면(/ads/*)은 (main) 밖이라 page_view 가 user_events 에 남지 않아
 *   신청 화면이 열릴 때 buy_view 단계를 직접 남긴다.
 */

export const ADS_FUNNEL_EVENT = 'ads_funnel';

/** 단계 키 → 어드민 라벨(화면 순서 = 배열 순서). */
export const ADS_FUNNEL_STEPS = [
  ['buy_view', '구매 화면(/ads/buy) 열림'],
  ['product', '상품 고름'],
  ['target', '대상(현장·시군구·홈) 고름'],
  ['period', '기간 고름(견적 표시)'],
  ['creative', '소재 이미지 준비'],
  ['review_pass', '자동 확인 통과'],
  ['review_fail', '자동 확인 걸림'],
  ['pay_click', '결제 버튼 누름'],
  ['login_wall', '결제 전 로그인으로 이동'],
  ['pay_open', '결제창 열림(자리 잡음)'],
  ['pay_success', '결제 성공'],
  ['pay_fail', '결제 실패·취소'],
  ['quote_request', '견적서 요청'],
] as const;

export type AdsFunnelStep = (typeof ADS_FUNNEL_STEPS)[number][0];
export const ADS_FUNNEL_STEP_KEYS: readonly AdsFunnelStep[] = ADS_FUNNEL_STEPS.map(([k]) => k);
export const isAdsFunnelStep = (v: unknown): v is AdsFunnelStep => typeof v === 'string' && (ADS_FUNNEL_STEP_KEYS as readonly string[]).includes(v);

/** properties 정리 — 짧은 문자열·숫자·불리언만, 키 8개까지(개인정보 칸 이름은 버린다). */
const PII_KEYS = /name|mail|phone|tel|biz|company|contact|addr/i;
export function cleanFunnelProps(p: Record<string, unknown> | null | undefined): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!p) return out;
  for (const [k, v] of Object.entries(p).slice(0, 8)) {
    if (PII_KEYS.test(k)) continue;
    if (typeof v === 'string') out[k] = v.slice(0, 40);
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

export interface FunnelEventRow { visitor_id: string | null; event_name: string | null; created_at: string }
export interface PageEventRow { visitor_id: string | null; page_path: string | null; created_at: string }
export interface FunnelStepRow { key: string; label: string; v7: number; v30: number }

/** 순수 — 방문자 수(7일·30일). visitor_id 가 없는 행은 세지 않는다. */
function distinctBy<T extends { visitor_id: string | null; created_at: string }>(rows: readonly T[], from7: number, pick: (r: T) => boolean): { v7: number; v30: number } {
  const s7 = new Set<string>(), s30 = new Set<string>();
  for (const r of rows) {
    if (!r.visitor_id || !pick(r)) continue;
    s30.add(r.visitor_id);
    if (Date.parse(r.created_at) >= from7) s7.add(r.visitor_id);
  }
  return { v7: s7.size, v30: s30.size };
}

/**
 * 순수 — 어드민 퍼널 표(방문자 기준). pages = /advertise page_view(30일), events = ads_funnel(30일).
 * 맨 앞 줄은 광고 안내 방문, 나머지는 단계 순서.
 */
export function funnelStepRows(pages: readonly PageEventRow[], events: readonly FunnelEventRow[], from7Iso: string): FunnelStepRow[] {
  const f7 = Date.parse(from7Iso);
  const pathOf = (p: string | null) => String(p ?? '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  const rows: FunnelStepRow[] = [
    { key: 'advertise_view', label: '광고 안내(/advertise) 방문', ...distinctBy(pages, f7, (r) => pathOf(r.page_path) === '/advertise') },
  ];
  for (const [k, label] of ADS_FUNNEL_STEPS) rows.push({ key: k, label, ...distinctBy(events, f7, (r) => r.event_name === k) });
  return rows;
}
